<template>
    <div v-if="showCompareView" class="compare">
        <div
            class="compare__section"
            :class="{
                'compare__section--exam-has-level-up-mode': examHasLevelUpMode,
            }"
        >
            <div class="compare__premium-features">
                <div class="compare__premium-header">
                    Premium Features
                </div>
                <div v-if="exam?.itemCount" class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/checkmark.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        {{ exam.itemCount - exam.archivedCount }} Questions and Explanations
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/checkmark.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Performance by Subject
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/checkmark.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Mobile & Web Access
                    </div>
                </div>
                <div
                    v-if="bundle && bundle.name && uniqueExams.length"
                    class="compare__compare-info"
                >
                    <img
                        src="@/assets/register-onboard/checkmark.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Prep for {{ uniqueExams.length }} {{ bundle.name }} Exams
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/checkmark.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Pass Guarantee
                    </div>
                </div>
            </div>
            <div class="compare__app-info">
                <div class="compare__premium-header">
                    Premium Quiz Modes
                </div>
                <div v-if="exam.mockExams && exam.mockExams.length" class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/mock-exam.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        {{ exam.mockExams.length }} Mock Exam{{ exam.mockExams.length > 1 ? 's' : '' }}
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/qotd.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Question of the Day
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/quick-ten.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Quick 10 Quiz
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/timed-quiz.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Timed Quiz
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/missed-questions.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Missed Questions Quiz
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/weakest-subject.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Weakest Subject Quiz
                    </div>
                </div>
                <div class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/pencil.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Build Your Own Quiz
                    </div>
                </div>
                <div v-if="examHasLevelUpMode" class="compare__compare-info">
                    <img
                        src="@/assets/register-onboard/level-up.svg"
                        class="compare__icon"
                        alt=""
                    >
                    <div>
                        Level Up Quiz
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import type { Study } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'
import { examMetadataModule } from '@/store/examMetadata/module'

const props = withDefaults(
    defineProps<{
        exam: Study.Class.ExamMetadataJSON | ClientExam
        showCompareView?: boolean
        bundle: Study.Class.BundleJSON | ClientBundle
        examHasLevelUpMode?: boolean
    }>(),
    {
        showCompareView: false,
        examHasLevelUpMode: false,
    }
)

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()
})

const uniqueExams = computed(() => {
    return props.bundle.exams.reduce<string[]>((acc, exam) => {
        const examNativeAppName = examMetadataById.value[exam.objectId].nativeAppName
        if (!acc.includes(examNativeAppName)) {
            acc.push(examNativeAppName)
        }
        return acc
    }, [])
})

onMounted(async () => {
    await examMetadataModule.actions.fetchExamMetadata()
})
</script>
<style lang="scss" scoped>
.compare {
     &__section {
        display: flex;
        justify-content: space-evenly;
        min-height: 331px;
        padding-bottom: 50px;
        background: $white;

        &--exam-has-level-up-mode {
            min-height: 375px;
        }

        @include breakpoint(brown-bear) {
            display: flex;
            justify-content: center;
            width: auto;
            min-height: 342px;
            padding-bottom: 0;
            margin-bottom: 32px;

            &--exam-has-level-up-mode {
                padding-bottom: 16px;
            }
        }

        @include breakpoint(black-bear) {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: auto;
            min-height: 456px;
            justify-content: flex-start;
            padding-bottom: 20px;
        }
    }

    &__premium-features {
        width: 331px;
        height: 237px;
        margin-top: 31px;
        margin-left: 12px;

        @include breakpoint(brown-bear) {
            margin-left: 0;
            margin-right: 0;
            width: 316px;
        }

        @include breakpoint(black-bear) {
            display: flex;
            flex-direction: column;
            height: fit-content;
            margin-left: 0;
            margin-right: 0;
            width: 319px;
        }
    }

    &__compare-info {
        display: flex;
        color: $brand-black;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;
        align-items: center;
        border-bottom: 1px solid $pewter;
        margin-bottom: 5px;
        padding-bottom: 2px;
        height: 35px;

        &:last-child {
            border: none;
            margin-bottom: 0
        }

        @include breakpoint(brown-bear) {
            width: 290px;
        }

        @include breakpoint(black-bear) {
            width: 312px;
        }
    }

    &__premium-header {
        color: $slate;
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
        margin-bottom: 7px;
    }

    &__app-info{
        width: 337px;
        height: 237px;
        margin-top: 31px;
        margin-left: 45px;

        @include breakpoint(brown-bear) {
            width: 293px;
            margin-right: 5px;
            height: fit-content;
            margin-left: 35px;
        }

        @include breakpoint(black-bear) {
            display: flex;
            flex-direction: column;
            height: fit-content;
            margin-left: 0;
            margin-right: 0;
            margin-top: 17px;
            width: 319px;
        }
    }

    &__info {
        display: flex;
        align-items: start;
        margin-bottom: 28px;
    }

    &__info-message {
        color: $brand-black;
        font-size: 15px;
        font-weight: 500;
        line-height: 22px;
        margin-top: 5px;
        margin-left: 15px;

        @include breakpoint(black-bear) {
            width: 277px;
        }
    }

    &__icon {
        margin-right: 5px;
        margin-left: 4px;

        @include breakpoint(black-bear) {
            margin-left: 0;
            margin-right: 8px;
        }
    }
}
</style>
